import React from 'react';
import PropTypes from 'prop-types';

import { IClassNames, makeClassNames } from '../../../utils/styles';

import './table.scss';

/**
 * IColumn interface
 * Each COLUMN has PROPS to RENDER both DATA and CSS class names
 * @param title - The NAME to show in TH element
 * @param colspan - The TH COLSPAN (use 0 to hide a column)
 * @param data - The NAME of the PROP in ROW array of objects containing DATA to RENDER
 * @param classModifier - NAME to APPEND to CLASS NAME for this COLUMN (STATIC, e.g. used for ALL ROWS) using BEM, see http://getbem.com/introduction/
 * @param classColumn - Append ROW DATA to CLASS NAME for this COLUMN (DYNAMIC, e.g. can change for each ROW) using BEM
 * @param render - Render prop (function to invoke when rendering this column cell, passing ROW DATA as PROPS)
 */
export interface IColumn {
  classColumn?: string;
  classModifier?: string;
  colspan?: number;
  data?: string;
  render?: Function;
  title?: string;
}

/**
 * IRow interface
 * Components which uses TABLE must EXTEND this interface
 * @param classRow - This function will be called passing ROW DATA and ROW INDEX as params; expects TRUE/FALSE in return
 */
export interface IRow {
  classRow?: Function;
  isBorderless?: boolean;
}

export interface ITableData<C, R> {
  className?: string; // ROOT NAME delle CLASSI CSS
  columns: C[]; // Array<IColumn>
  rows: R[]; // Array<IRow>
  showTitles?: boolean; // Visibilità del THEAD
}

const classNames = (className: string, showTitles: boolean): IClassNames => {
  return makeClassNames({
    table: className,
    head: [`${className}__head`, !showTitles && `${className}__head--hidden`],
    headRow: `${className}__head-row`,
    headCell: `${className}__head-cell`,
    body: `${className}__body`,
    bodyRow: `${className}__body-row`,
    bodyRowBorderless: `${className}__body-row--borderless`,
    bodyRowSelected: `${className}__body-row--selected`,
    bodyCell: `${className}__body-cell`,
    bodyCellSpan: `${className}__body-cell-span`,
  });
};

/**
 * Renders a TABLE passing an ARRAY of COLUMNS and an ARRAY of ROWS
 * Each COLUMN specify which DATA to RENDER from each ROW
 * @param columns - An ARRAY of OBJECTS, see interface IColumn
 * @param rows - An ARRAY of OBJECTS
 * @param className - The name to use for generating CSS class names
 * @param showTitles - CSS visibility of THEAD
 */
const Table = <C extends IColumn, R extends IRow>({
  /* istanbul ignore next: default value */ className = 'table',
  columns,
  rows,
  /* istanbul ignore next: default value */ showTitles = true,
}: ITableData<C, R>) => {
  const cn = classNames(className, showTitles);

  return (
    <table className={cn.table}>
      <thead className={cn.head}>
        <tr className={cn.headRow}>
          {columns.map((column: C, key: number) => {
            // COLSPAN=0 => SKIP TH
            const colspan = column.hasOwnProperty('colspan') ? column.colspan : 1;
            const classModifier = column.hasOwnProperty('classModifier')
              ? `${cn.headCell} ${cn.headCell}--${column.classModifier}`
              : cn.headCell;
            return colspan === 0 ? null : (
              <th key={key} colSpan={colspan} className={classModifier}>
                {column.title}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody className={cn.body}>
        {rows &&
          rows.map((row: R, index: number) => {
            // classRow = function(rowData, rowIndex) => returns true/false for EACH ROW
            const classRow = typeof row.classRow === 'function' && row.classRow(row, index) ? cn.bodyRowSelected : '';
            return (
              <tr key={index} className={`${cn.bodyRow} ${classRow} ${row?.isBorderless ? cn.bodyRowBorderless : ''}`}>
                {columns.map((column: C, key: number) => {
                  const classModifier: string = column.hasOwnProperty('classModifier')
                    ? `${cn.bodyCell}--${column.classModifier}`
                    : '';
                  const classColumn =
                    column.hasOwnProperty('classColumn') && row.hasOwnProperty(column.classColumn!)
                      ? // @ts-ignore
                        `${cn.bodyCell}--${row[column.classColumn!]}`
                      : '';
                  // @ts-ignore
                  const data = column.data && row.hasOwnProperty(column.data) ? row[column.data] : ' ';

                  return (
                    <td key={key} className={`${cn.bodyCell} ${classModifier} ${classColumn}`}>
                      <span className={cn.bodyCellSpan}>
                        {typeof column.render === 'function' ? column.render(data) : data}
                      </span>
                    </td>
                  );
                })}
              </tr>
            );
          })}
      </tbody>
    </table>
  );
};

Table.propTypes = {
  className: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      classColumn: PropTypes.string,
      classModifier: PropTypes.string,
      colspan: PropTypes.number,
      data: PropTypes.string,
      render: PropTypes.func,
      title: PropTypes.string,
    }).isRequired
  ).isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      classRow: PropTypes.func,
    }).isRequired
  ).isRequired,
  showTitles: PropTypes.bool,
};

export default Table;
